import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Deduction } from '../../../types';
import Input from '../Input';
import Select from '../Select';
import AsyncSelect from '../AsyncSelect';
import { resolveCurrencyCode } from '../../../utils/currencyUtils';

interface DeductionHeaderSectionProps {
  register: UseFormRegister<Deduction>;
  errors: FieldErrors<Deduction>;
  clientOptions: { value: string; label: string }[];
  vesselOptions: { value: string; label: string }[];
  invoiceValue: string;
  onInvoiceChange: (value: string) => void;
  onInvoiceSelect: (invoice: any | null) => void;
  fetchInvoiceOptions: (query: string, page: number) => Promise<any>;
  selectedInvoice: any | null;
}

const invoiceLabel = (inv: any): string => {
  const number = inv?.invoiceNumber || `#${inv?.id}`;
  const code = resolveCurrencyCode(inv?.currency);
  const total = Number(inv?.originalTotal ?? inv?.netTotal ?? 0).toLocaleString();
  const customer = inv?.customer?.name ? ` — ${inv.customer.name}` : '';
  return `${number}${customer} — ${code} ${total}`;
};

const DeductionHeaderSection: React.FC<DeductionHeaderSectionProps> = ({
  register,
  errors,
  clientOptions,
  vesselOptions,
  invoiceValue,
  onInvoiceChange,
  onInvoiceSelect,
  fetchInvoiceOptions,
  selectedInvoice,
}) => {
  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Deduction Information</h2>
        </div>
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Input
                label="Deduction Number"
                {...register('deductionNumber', { required: 'Deduction number is required' })}
                error={errors.deductionNumber?.message}
              />
            </div>
            <div>
              <AsyncSelect
                label="Invoice"
                name="invoiceId"
                value={invoiceValue}
                onChange={onInvoiceChange}
                onSelectItem={onInvoiceSelect}
                fetchOptions={fetchInvoiceOptions}
                getOptionLabel={invoiceLabel}
                getOptionValue={(inv: any) => inv.documentId || inv.id?.toString()}
                placeholder="Search invoice by number..."
                error={errors.invoiceId?.message}
              />
            </div>
            <div>
              <Input
                label="Deduction Date"
                type="date"
                {...register('deductionDate', { required: 'Deduction date is required' })}
                error={errors.deductionDate?.message}
              />
            </div>
          </div>
          {selectedInvoice && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Invoice Info:</strong>{' '}
                {(() => {
                  const code = resolveCurrencyCode(selectedInvoice.currency);
                  const number = selectedInvoice.invoiceNumber || `#${selectedInvoice.id}`;
                  const total = Number(selectedInvoice.originalTotal ?? selectedInvoice.netTotal ?? 0).toLocaleString();
                  const remaining = Number(selectedInvoice.netTotal ?? 0).toLocaleString();
                  const customer = selectedInvoice.customer?.name ? ` | ${selectedInvoice.customer.name}` : '';
                  return `Invoice ${number}${customer} | Total: ${code} ${total} | Remaining: ${code} ${remaining}`;
                })()}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Client & Vessel Details</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Select
                label="Client"
                {...register('clientId', { required: 'Client is required' })}
                options={clientOptions}
                error={errors.clientId?.message}
                helperText="Auto-filled when an invoice is selected"
              />
            </div>
            <div>
              <Select
                label="Vessel"
                {...register('vesselId')}
                options={vesselOptions}
                placeholder="Select vessel (optional)"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeductionHeaderSection;
